import styled from '@emotion/styled'
import { Props as ImageProps } from 'app/components/Common/Image'
import { Scroll } from 'app/components/Common/Scroll'
import { Media } from 'app/theme/media'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'
import { Parallax } from 'react-scroll-parallax'

import { Youtube } from './Youtube'

export interface Props {
  claim?: string
  idVideo?: string
  idVideoMobile?: string
  mobilePlaceholder?: ImageProps
  payoff?: string
  placeholder?: ImageProps
  siteName?: string
}

export const HeroVideo = memo(function HeroVideo({
  claim,
  idVideo,
  idVideoMobile,
  mobilePlaceholder,
  payoff,
  placeholder,
  siteName,
}: Props) {
  const [animObjectRef, isAnimObjectVisible] = useInView({ triggerOnce: true })

  return (
    <Container>
      <Parallax className="wrapper" translateY={['-20%', '20%']}>
        <Media lessThan="ipadVertical">
          {idVideoMobile ? (
            <Youtube ID={idVideoMobile} placeholder={mobilePlaceholder} />
          ) : null}
        </Media>

        <Media greaterThanOrEqual="ipadVertical">
          {idVideo ? <Youtube ID={idVideo} placeholder={placeholder} /> : null}
        </Media>
      </Parallax>

      {payoff ? (
        <Text
          className={isAnimObjectVisible ? 'visible' : undefined}
          ref={animObjectRef}
        >
          <Payoff>{payoff}</Payoff>

          {claim ? <Claim>{claim}</Claim> : null}

          {siteName ? <SiteName>{siteName}</SiteName> : null}
        </Text>
      ) : null}

      <Scroll />
    </Container>
  )
})

const Container = styled.section`
  height: 100vh;
  max-height: -webkit-fill-available;
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  overflow: hidden;
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    opacity: 0.25;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
  }

  .wrapper {
    height: 100%;
    position: relative;
    z-index: 2;
  }
`

const Payoff = styled.h1`
  max-width: 30rem;
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 3.75rem;
  font-weight: 300;
  line-height: 3.75rem;
  opacity: 0;
  margin: auto;
  text-transform: uppercase;
  transform: translateY(2.5rem);
  transition: 0.8s 2.5s ease-in-out;

  @media (max-width: 1199px) {
    max-width: none;
    font-size: 2.5rem;
    line-height: 2.5rem;
  }
`

const Claim = styled.h2`
  max-width: 30rem;
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 3.75rem;
  font-weight: 500;
  line-height: 3.75rem;
  opacity: 0;
  margin: auto;
  text-transform: uppercase;
  transform: translateY(2.5rem);
  transition: 0.8s 2.5s ease-in-out;

  @media (max-width: 1199px) {
    max-width: none;
    font-size: 2.5rem;
    line-height: 2.5rem;
  }
`

const SiteName = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  opacity: 0;
  margin-top: 1.5rem;
  transform: translateY(2.5rem);
  transition: 0.8s 2.7s ease-in-out;

  @media (max-width: 1199px) {
    margin-top: 0.875rem;
  }
`

const Text = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  position: absolute;
  top: 50%;
  right: 1.875rem;
  left: 1.875rem;
  text-align: center;
  transform: translateY(-50%);
  z-index: 3;
  &.visible {
    ${Payoff},
    ${Claim},
    ${SiteName} {
      opacity: 1;
      transform: none;
    }
  }
`
